import React from 'react';
import { useOrgContext } from '@/contexts/OrgContext';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2, FileText, Plus, Eye } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router';
import { PageTopNav } from '@/components/nav/PageTopNav';
import { Link } from 'react-router';
import { RecordCard } from '@/components/ui/RecordCard';
import { formatDate } from '@/lib/utils';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';

// Define the Record type based on API response
interface Record {
  id: string;
  englishName: string;
  scientificName: string;
  dtm: string;
  count: number;
  lat: number;
  lng: number;
  note: string;
  locationNote: string;
  createdAt: string;
  updatedAt: string;
}

const OrgRecords: React.FC = () => {
  const navigate = useNavigate();
  const { orgRole, organisation } = useOrgContext();

  // Infinite scroll setup
  const TAKE = 30;
  const {
    data,
    isLoading,
    isError: error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['organizationRecords', organisation?.id],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await fetch(`/api/org/${organisation?.id}/records?skip=${pageParam}&take=${TAKE}`);
      if (!response.ok) {
        throw new Error('Failed to fetch records');
      }
      return response.json() as Promise<Record[]>;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < TAKE) return undefined;
      return allPages.flat().length;
    },
    enabled: !!organisation?.id,
    initialPageParam: 0,
  });

  // Flatten all loaded records
  const records = data?.pages.flat() ?? [];

  // Infinite scroll trigger
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  const desktopScrollRef = React.useRef<HTMLDivElement | null>(null);

  // Utility: detect desktop
  const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;

  React.useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    // Use scroll container as root on desktop
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      {
        root: isDesktop ? desktopScrollRef.current : null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isDesktop]);

  return (
    <div className="container mx-auto">
      <PageTopNav
        pageName={`${organisation?.name} Records`}
        rightItem={orgRole === 'ADMIN' ? (
          <Link to={`/app/org/${organisation?.id}/records/add`}>
            <Button>
              <Plus className="h-4 w-4" />
              <span className="hidden md:block">Add Record</span>
            </Button>
          </Link>
        ) : null}
      />

      {isLoading ? (
        <div className="flex justify-center p-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          Failed to load records. Please try again.
        </div>
      ) : (
        <div className="space-y-4">
          {records && records.length > 0 ? (
            <>
              {/* Mobile: RecordCard List */}
              <div className="block md:hidden space-y-4">
                {records.map((record) => (
                  <RecordCard
                    key={record.id}
                    record={{
                      ...record,
                      orgName: organisation?.name ?? null,
                      orgId: organisation?.id ?? null,
                    }}
                    onClick={() => navigate(`/app/org/${organisation?.id}/record/${record.id}`)}
                  />
                ))}
                {/* Infinite scroll trigger */}
                <div ref={loadMoreRef} />
                {isFetchingNextPage && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                )}
                {!hasNextPage && (
                  <div className="text-center text-muted-foreground text-xs py-2">No more records</div>
                )}
              </div>
              {/* Desktop: Shadcn Table */}
              <div className="hidden md:block overflow-auto max-h-[80vh]" ref={desktopScrollRef}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Species ID</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Count</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.map((record) => (
                      <TableRow key={record.id} className="cursor-pointer" onClick={() => navigate(`/app/org/${organisation?.id}/record/${record.id}`)}>
                        <TableCell>{record.englishName}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span>{formatDate(record.dtm)}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(record.dtm), { addSuffix: true })}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{record.count || '-'}</TableCell>
                        <TableCell>
                          <div className="max-w-[200px] truncate" title={record.note || '-'}>
                            {record.note || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {record.lat && record.lng ? (
                            <div className="text-xs">
                              <div>Lat: {record.lat.toFixed(5)}</div>
                              <div>Lng: {record.lng.toFixed(5)}</div>
                            </div>
                          ) : (
                            <span>{record.locationNote || '-'}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              navigate(`/app/org/${organisation?.id}/record/${record.id}`);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Infinite scroll trigger for desktop */}
                    <tr>
                      <td colSpan={6} style={{ padding: 0, border: 'none', background: 'transparent' }}>
                        <div ref={loadMoreRef} />
                        {isFetchingNextPage && (
                          <div className="flex justify-center py-4">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                          </div>
                        )}
                        {!hasNextPage && (
                          <div className="text-center text-muted-foreground text-xs py-2">No more records</div>
                        )}
                      </td>
                    </tr>
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center p-4 bg-muted/50 rounded-md">
              <FileText className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">No records found in this organization.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrgRecords;
